import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import api from '../api/client';
import { useToast } from './ToastNotifications';
import './PlotViewer.css';
import {
  ETLRunsSection,
  QueryFormSection,
  TagFilterRow,
  SpeciesFilterRow,
  SelectionActionsRows,
  ImageGridSection,
  ImageLightbox,
  normalizeTags,
  matchesSpeciesFilters,
  SPECIES_NONE_TOKEN,
} from './dataviewer/index';
import type { ETLRun, ImageWithTags, PresignedUrlResponse, QueryImageData, QueryImageDataLabel } from './dataviewer/types';

function isCompletedRun(run: ETLRun) {
  return String(run.status ?? '').split('.').pop()?.toUpperCase() === 'COMPLETED';
}

const DataViewer: React.FC = () => {
  // ETL Runs state
  const [etlRuns, setEtlRuns] = useState<ETLRun[]>([]);
  const [searchRunName, setSearchRunName] = useState<string>('');
  const [searchStartDate, setSearchStartDate] = useState<string>('');
  const [searchEndDate, setSearchEndDate] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });
  const completedRuns = useMemo(() => etlRuns.filter(isCompletedRun), [etlRuns]);

  // Calculate dates with run starts and dates with data for calendar
  const { datesWithRunStarts, datesWithData } = React.useMemo(() => {
    const runStarts = new Set<string>();
    const hasData = new Set<string>();
    
    completedRuns.forEach((run) => {
      if (run.start_time) {
        // Grab date from a UTC string
        const startDateStr = run.start_time.split('T')[0];
        const endDateStr = run.end_time ? run.end_time.split('T')[0] : startDateStr;
        
        runStarts.add(startDateStr);
        
        hasData.add(startDateStr);
        
        if (startDateStr !== endDateStr) {
          const cursor = new Date(`${startDateStr}T00:00:00Z`);
          const end = new Date(`${endDateStr}T00:00:00Z`);

          while (cursor.getTime() <= end.getTime()) {
            hasData.add(cursor.toISOString().split('T')[0]);
            cursor.setUTCDate(cursor.getUTCDate() + 1);
          }
        }
      }
    });
    
    return {
      datesWithRunStarts: Array.from(runStarts).sort(),
      datesWithData: Array.from(hasData).sort()
    };
  }, [completedRuns]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [runsError, setRunsError] = useState<string>('');

  // Image Viewer state
  const [robotName, setRobotName] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string>('');
  const [images, setImages] = useState<ImageWithTags[]>([]);
  const [presignedUrls, setPresignedUrls] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [activePicselliaUploads, setActivePicselliaUploads] = useState(0);
  const { showToast } = useToast();
  const [tagInput, setTagInput] = useState('');
  const [addingTags, setAddingTags] = useState(false);
  const [removingTag, setRemovingTag] = useState(false);
  const [tagToRemove, setTagToRemove] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<{ url: string; presignedUrl: string } | null>(null);
  const [queryImageData, setQueryImageData] = useState<QueryImageData | null>(null);
  const [queryImageDataLoading, setQueryImageDataLoading] = useState(false);
  const [classificationInput, setClassificationInput] = useState('');
  const [savingClassifications, setSavingClassifications] = useState(false);
  const [classificationBatchInput, setClassificationBatchInput] = useState('');
  const [addingClassificationsBatch, setAddingClassificationsBatch] = useState(false);
  const [classificationToRemove, setClassificationToRemove] = useState('');
  const [removingClassification, setRemovingClassification] = useState(false);
  const [classificationsForSelectedImages, setClassificationsForSelectedImages] = useState<Record<string, string[]>>({});
  const [queryTemplates, setQueryTemplates] = useState<{ [key: string]: { name: string; description: string } }>({});
  const [selectedQueryTemplate, setSelectedQueryTemplate] = useState<string>('default');
  const [includeTags, setIncludeTags] = useState<string[]>([]);
  const [excludeTags, setExcludeTags] = useState<string[]>([]);
  const [includeTagInput, setIncludeTagInput] = useState<string>('');
  const [excludeTagInput, setExcludeTagInput] = useState<string>('');
  const [includeSpecies, setIncludeSpecies] = useState<string[]>([]);
  const [includeSpeciesInput, setIncludeSpeciesInput] = useState<string>('');
  const [excludeSpecies, setExcludeSpecies] = useState<string[]>([]);
  const [excludeSpeciesInput, setExcludeSpeciesInput] = useState<string>('');
  const [speciesInput, setSpeciesInput] = useState('');
  const [settingSpecies, setSettingSpecies] = useState(false);
  const [clearingSpecies, setClearingSpecies] = useState(false);
  const [loadedImageDims, setLoadedImageDims] = useState<
    Record<string, { width: number; height: number }>
  >({});
  const [savingImageDims, setSavingImageDims] = useState(false);
  const [filterVersion, setFilterVersion] = useState(0);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [imagesPerPage] = useState<number>(50);
  const [useInfiniteScroll, setUseInfiniteScroll] = useState<boolean>(false);
  const [visibleImageCount, setVisibleImageCount] = useState<number>(50); // For infinite scroll
  const imageContainerRef = React.useRef<HTMLDivElement>(null);

  // Load query templates on mount
  useEffect(() => {
    loadQueryTemplates();
  }, []);

  // Fetch image data (labels/classifications) when lightbox opens for query viewer
  useEffect(() => {
    if (!selectedImage) {
      setQueryImageData(null);
      return;
    }
    let cancelled = false;
    setQueryImageDataLoading(true);
    setQueryImageData(null);
    api.dataviewer.getQueryImageData(selectedImage.url)
      .then((res) => {
        if (!cancelled && res.data) setQueryImageData(res.data);
      })
      .catch(() => {
        if (!cancelled) setQueryImageData(null);
      })
      .finally(() => {
        if (!cancelled) setQueryImageDataLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedImage]);

  const currentClassificationNames = React.useMemo(() => {
    if (!queryImageData?.ground_truth_labels) return [];
    return queryImageData.ground_truth_labels
      .filter((l: QueryImageDataLabel) => l.type === 'classification' || !l.bbox)
      .map((l: QueryImageDataLabel) => l.class_name);
  }, [queryImageData]);

  const confirmClassificationChange = (
    action: 'add' | 'remove',
    classifications: string[],
    targetDescription: string,
  ) => {
    if (classifications.length === 0) return false;
    const classificationLabel = classifications.length === 1 ? 'classification' : 'classifications';
    const verb = action === 'add' ? 'add' : 'remove';
    return window.confirm(
      `Are you sure you want to ${verb} ${classificationLabel} "${classifications.join(', ')}" ${action === 'add' ? 'for' : 'from'} ${targetDescription}?`
    );
  };

  const addClassification = async () => {
    const label = classificationInput.trim();
    if (!label || !selectedImage || savingClassifications) return;
    if (!confirmClassificationChange('add', [label], 'this image')) return;
    setSavingClassifications(true);
    try {
      const newList = [...currentClassificationNames, label];
      await api.dataviewer.addClassifications({
        image_url: selectedImage.url,
        classifications: newList,
      });
      setClassificationInput('');
      const res = await api.dataviewer.getQueryImageData(selectedImage.url);
      if (res.data) setQueryImageData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add classification');
    } finally {
      setSavingClassifications(false);
    }
  };

  const removeClassification = async (classToRemove: string) => {
    if (!selectedImage || savingClassifications) return;
    if (!confirmClassificationChange('remove', [classToRemove], 'this image')) return;
    setSavingClassifications(true);
    try {
      const newList = currentClassificationNames.filter((c: string) => c !== classToRemove);
      await api.dataviewer.addClassifications({
        image_url: selectedImage.url,
        classifications: newList,
      });
      const res = await api.dataviewer.getQueryImageData(selectedImage.url);
      if (res.data) setQueryImageData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to remove classification');
    } finally {
      setSavingClassifications(false);
    }
  };

  const handleAddClassificationsBatch = async () => {
    const raw = classificationBatchInput.split(/\s*,\s*/).map((s) => s.trim()).filter(Boolean);
    if (raw.length === 0 || selectedImages.size === 0) {
      if (selectedImages.size === 0) setError('Select at least one image');
      return;
    }
    if (!confirmClassificationChange('add', raw, `${selectedImages.size} selected image${selectedImages.size === 1 ? '' : 's'}`)) return;
    setAddingClassificationsBatch(true);
    setError('');
    try {
      const res = await api.dataviewer.addClassifications({
        image_urls: Array.from(selectedImages),
        classifications: raw,
      });
      const data = res.data as { successful_updates?: number; updated_count?: number; total_records?: number; total_requested?: number; errors?: string[] };
      const count = data.successful_updates ?? data.updated_count ?? 0;
      const total = data.total_records ?? data.total_requested ?? selectedImages.size;
      if (count > 0) {
        setClassificationBatchInput('');
        setError('');
        if (data.errors?.length) {
          setError(`Added to ${count}/${total} images. Some errors: ${data.errors.slice(0, 3).join('; ')}`);
        }
      } else if (data.errors?.length) {
        setError(data.errors.slice(0, 5).join('; '));
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add classification');
    } finally {
      setAddingClassificationsBatch(false);
    }
  };

  const loadETLRuns = useCallback(async () => {
    setLoadingRuns(true);
    setRunsError('');
    try {
      const response = await api.dataviewer.fetchAllETLRuns({
        run_name: searchRunName || undefined,
        start_date: searchStartDate || undefined,
        end_date: searchEndDate || undefined,
        sort_by: 'start_time',
      });
      setEtlRuns(response.data.runs);
    } catch (err: any) {
      setRunsError(`Error loading ETL runs: ${err.message}`);
    } finally {
      setLoadingRuns(false);
    }
  }, [searchRunName, searchStartDate, searchEndDate]);

  // Initialize search dates to current month on mount
  useEffect(() => {
    if (!searchStartDate && !searchEndDate) {
      const [year, month] = currentMonth.split('-').map(Number);
      const firstDay = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
      setSearchStartDate(firstDay);
      setSearchEndDate(lastDay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed month range once when dates are empty
  }, []);

  useEffect(() => {
    loadETLRuns();
  }, [loadETLRuns]);

  const filteredETLRuns = React.useMemo(() => {
    if (!selectedDate) {
      const [year, month] = currentMonth.split('-').map(Number);
      const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
      const monthEnd = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
      
      return completedRuns.filter((run) => {
        if (!run.start_time) return false;
        
        const runStartDate = run.start_time.split('T')[0];
        const runEndDate = run.end_time ? run.end_time.split('T')[0] : runStartDate;
        
        return runStartDate <= monthEnd && runEndDate >= monthStart;
      });
    }
    
    // Filter by selected date
    return completedRuns.filter((run) => {
      if (!run.start_time) return false;
      
      const runStartDate = run.start_time.split('T')[0];
      const runEndDate = run.end_time ? run.end_time.split('T')[0] : runStartDate;
      
      return selectedDate >= runStartDate && selectedDate <= runEndDate;
    });
  }, [completedRuns, selectedDate, currentMonth]);

  const loadQueryTemplates = async () => {
    try {
      const response = await api.dataviewer.getQueryTemplates();
      console.log('Query templates response:', response.data);
      if (response.data && Object.keys(response.data).length > 0) {
        setQueryTemplates(response.data);
        console.log('Loaded query templates:', Object.keys(response.data));
      } else {
        console.warn('No query templates in response, using fallback');
        // Fallback to default if no templates loaded
        setQueryTemplates({ default: { name: 'default', description: 'All images from the run' } });
      }
    } catch (err: any) {
      console.error('Error loading query templates:', err);
      // Fallback to default on error
      setQueryTemplates({ default: { name: 'default', description: 'All images from the run' } });
    }
  };

  const handleRunClick = (run: ETLRun) => {
    // Populate image viewer with run data (all times are already UTC, no conversion needed)
    setRobotName(run.robot_name);
    if (run.start_time) {
      // Extract UTC components directly from ISO string (no Date object conversion)
      // run.start_time is already in UTC format like "2026-01-07T06:36:00.000Z"
      const startMatch = run.start_time.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (startMatch) {
        const [, year, month, day, hour, minute] = startMatch;
        setStartDate(`${year}-${month}-${day}T${hour}:${minute}`);
      }
    }
    if (run.end_time) {
      // Extract UTC components directly from ISO string (no Date object conversion)
      const endMatch = run.end_time.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (endMatch) {
        const [, year, month, day, hour, minute] = endMatch;
        setEndDate(`${year}-${month}-${day}T${hour}:${minute}`);
      }
    }
  };

  const handleQuery = async () => {
    if (!robotName || !startDate || !endDate) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');
    setJobId(null);
    setImages([]);
    setPresignedUrls(new Map());
    setLoadedImageDims({});
    setSelectedImages(new Set());
    setIncludeTags([]);
    setExcludeTags([]);
    setIncludeTagInput('');
    setExcludeTagInput('');
    setIncludeSpecies([]);
    setIncludeSpeciesInput('');
    setExcludeSpecies([]);
    setExcludeSpeciesInput('');
    setFilterVersion(0);
    setCurrentPage(1);
    setLastSelectedIndex(-1);

    try {
      // Convert datetime-local to ISO format for API (treat as UTC, no timezone conversion)
      const startDateObj = new Date(startDate + ':00.000Z');
      const endDateObj = new Date(endDate + ':00.000Z');
      const startISO = startDateObj.toISOString();
      const endISO = endDateObj.toISOString();

      const response = await api.dataviewer.query({
        robot_name: robotName,
        start_date: startISO,
        end_date: endISO,
        query_template: selectedQueryTemplate,
      });
      setJobId(response.data.job_id);
      setJobStatus(response.data.status);
    } catch (err: any) {
      setError(`Error submitting query: ${err.message}`);
      setLoading(false);
    }
  };

  const loadImagesFromData = useCallback(async (imageData: ImageWithTags[]) => {
    setImages(imageData);
    setLoadedImageDims({});
    setImageLoading(true);

    try {
      const s3Urls = imageData.map((img) => img.s3_url);
      const response = await api.dataviewer.getPresignedUrls(s3Urls);

      const urlMap = new Map<string, string>();
      response.data.urls.forEach((item: PresignedUrlResponse) => {
        if (item.presigned_url) {
          urlMap.set(item.s3_url, item.presigned_url);
        }
      });
      setPresignedUrls(urlMap);

    } catch (err: any) {
      setError(`Error loading presigned URLs: ${err.message}`);
    } finally {
      setImageLoading(false);
    }
  }, []);

  const loadJobResult = useCallback(async (jid: string) => {
    setImageLoading(true);
    setError('');
    try {
      const response = await api.dataviewer.getJobResults(jid);
      const rawList = Array.isArray(response.data) ? response.data : (response.data as any)?.results ?? (response.data as any)?.data ?? [];
      const imageData: ImageWithTags[] = rawList.flatMap((item: any) => {
        if (!item || typeof item !== 'object') return [];
        const entries = Object.entries(item);
        if (entries.length === 0) return [];
        const [s3_url, rawTags] = entries[0];
        if (typeof s3_url !== 'string' || !s3_url) return [];
        const tags = rawTags != null && typeof rawTags === 'object' && 'tags' in rawTags
          ? normalizeTags((rawTags as { tags: unknown }).tags)
          : normalizeTags(rawTags);
        const species = rawTags != null && typeof rawTags === 'object' && 'species' in rawTags
          ? String((rawTags as { species?: unknown }).species ?? '').trim() || null
          : null;
        return [{ s3_url, tags, species }];
      });
      await loadImagesFromData(imageData);
    } catch (err: any) {
      setError(`Error loading results: ${err.message}`);
    } finally {
      setImageLoading(false);
    }
  }, [loadImagesFromData]);

  // Poll job status when jobId is set
  useEffect(() => {
    if (!jobId) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await api.dataviewer.getJobStatus(jobId);
        setJobStatus(response.data.status);

        if (response.data.status === 'completed') {
          clearInterval(pollInterval);
          setLoading(false);
          await loadJobResult(jobId);
        } else if (response.data.status === 'failed') {
          clearInterval(pollInterval);
          setLoading(false);
          setError(response.data.error || 'Job failed');
        }
      } catch (err: any) {
        console.error('Error polling job status:', err);
        clearInterval(pollInterval);
        setError(`Error checking job status: ${err.message}`);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [jobId, loadJobResult]);

  const fetchMetadataForChunk = async (
    chunk: string[]
  ): Promise<Record<string, { tags: string[]; species?: string | null }>> => {
    try {
      const res = await api.dataviewer.getMetadataBatch(chunk);
      return res.data.results || {};
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        const res = await api.dataviewer.getTagsBatch(chunk);
        const out: Record<string, { tags: string[] }> = {};
        for (const [url, tags] of Object.entries(res.data.results || {})) {
          out[url] = { tags: tags ?? [] };
        }
        return out;
      }
      throw err;
    }
  };

  const refreshImageMetadata = async (s3Urls: string[]) => {
    if (s3Urls.length === 0) return;
    const urlSet = new Set(s3Urls);
    const METADATA_CHUNK = 500;
    try {
      const merged: Record<string, { tags: string[]; species?: string | null }> = {};
      for (let i = 0; i < s3Urls.length; i += METADATA_CHUNK) {
        const chunk = s3Urls.slice(i, i + METADATA_CHUNK);
        Object.assign(merged, await fetchMetadataForChunk(chunk));
      }
      setImages((prev) =>
        prev.map((img) => {
          if (!urlSet.has(img.s3_url)) return img;
          const meta = merged[img.s3_url];
          if (!meta) return img;
          const nextSpecies =
            meta.species !== undefined
              ? meta.species != null && String(meta.species).trim()
                ? String(meta.species).trim()
                : null
              : img.species;
          return {
            ...img,
            tags: normalizeTags(meta.tags),
            species: nextSpecies,
          };
        })
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to refresh image metadata after Picsellia upload:', message);
    }
  };

  const pollPicselliaUploadJob = (jobId: string, s3Urls: string[]) => {
    let missing = 0;
    const pollInterval = window.setInterval(async () => {
      try {
        const response = await api.picsellia.getUploadJobStatus(jobId);
        missing = 0;
        const status = response.data.status;

        if (status === 'completed') {
          window.clearInterval(pollInterval);
          setActivePicselliaUploads((n) => Math.max(0, n - 1));
          const uploaded = response.data.uploaded_count ?? 0;
          const skipped = response.data.skipped_already_in_picsellia ?? 0;
          const msg =
            skipped > 0
              ? `Picsellia upload complete: ${uploaded} uploaded, ${skipped} already in Picsellia (skipped).`
              : `Picsellia upload complete: ${uploaded} image(s) uploaded.`;
          showToast('success', msg, { dismissible: true });
          void refreshImageMetadata(s3Urls);
        } else if (status === 'failed') {
          window.clearInterval(pollInterval);
          setActivePicselliaUploads((n) => Math.max(0, n - 1));
          showToast(
            'error',
            response.data.error || response.data.message || 'Picsellia upload failed',
            { dismissible: true }
          );
        }
      } catch (err: unknown) {
        const httpStatus = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (httpStatus === 404 && missing < 5) {
          missing += 1;
          return;
        }
        window.clearInterval(pollInterval);
        setActivePicselliaUploads((n) => Math.max(0, n - 1));
        const message = err instanceof Error ? err.message : 'Unknown error';
        showToast('error', `Error checking upload status: ${message}`, { dismissible: true });
      }
    }, 2000);
  };

  const handleUploadToPicsellia = async () => {
    if (selectedImages.size === 0) {
      setError('Please select at least one image to upload');
      return;
    }

    const s3Links = Array.from(selectedImages);
    const imageCount = s3Links.length;
    setSelectedImages(new Set());
    setError('');

    try {
      const response = await api.picsellia.sendToPicsellia({
        s3_links: s3Links,
        datalake_name: 'la-poseidon-datasets',
      });
      setActivePicselliaUploads((n) => n + 1);
      showToast(
        'info',
        `Picsellia upload queued (${imageCount} image${imageCount === 1 ? '' : 's'})`
      );
      pollPicselliaUploadJob(response.data.job_id, s3Links);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Error submitting Picsellia upload: ${message}`);
    }
  };

  const handleAddTags = async () => {
    const tags = tagInput.split(/\s*,\s*/).map((s) => s.trim()).filter(Boolean);
    if (tags.length === 0) {
      setError('Enter at least one tag (comma-separated)');
      return;
    }
    if (selectedImages.size === 0) {
      setError('Select at least one image');
      return;
    }

    setAddingTags(true);
    setError('');

    try {
      const res = await api.dataviewer.addTags({
        image_urls: Array.from(selectedImages),
        tags,
      });
      const data = res.data;
      if (data.errors && data.errors.length > 0) {
        setError(`Add tags: ${data.successful_updates} updated; errors: ${data.errors.join('; ')}`);
      } else {
        setTagInput('');
        if (data.successful_updates !== undefined && data.successful_updates > 0) {
          setError('');
        }
      }
    } catch (err: any) {
      setError(`Error adding tags: ${err.message}`);
    } finally {
      setAddingTags(false);
    }
  };

  const handleRemoveTag = async () => {
    if (!tagToRemove.trim() || selectedImages.size === 0) return;
    setRemovingTag(true);
    setError('');
    try {
      const res = await api.dataviewer.removeTag({
        image_urls: Array.from(selectedImages),
        tag: tagToRemove.trim(),
      });
      const data = res.data;
      if (data.errors && data.errors.length > 0) {
        setError(`Remove tag: ${data.successful_updates} updated; errors: ${data.errors.join('; ')}`);
      } else {
        setTagToRemove('');
        if (data.successful_updates !== undefined && data.successful_updates > 0) {
          setError('');
        }
      }
    } catch (err: any) {
      setError(`Error removing tag: ${err.message}`);
    } finally {
      setRemovingTag(false);
    }
  };

  const handleSetSpecies = async () => {
    const species = speciesInput.trim();
    if (!species || selectedImages.size === 0) {
      if (!species) setError('Enter a species value');
      if (selectedImages.size === 0) setError('Select at least one image');
      return;
    }

    const urls = Array.from(selectedImages);
    setSettingSpecies(true);
    setError('');
    try {
      const res = await api.dataviewer.setSpecies({
        image_urls: urls,
        species,
      });
      const data = res.data;
      if (data.successful_updates !== undefined && data.successful_updates > 0) {
        setSpeciesInput('');
        setImages((prev) =>
          prev.map((img) => (urls.includes(img.s3_url) ? { ...img, species } : img))
        );
        if (data.errors && data.errors.length > 0) {
          setError(`Set species: ${data.successful_updates} updated; some errors: ${data.errors.slice(0, 5).join('; ')}`);
        } else {
          setError('');
        }
      } else if (data.errors && data.errors.length > 0) {
        setError(`Set species: ${data.errors.join('; ')}`);
      }
    } catch (err: any) {
      setError(`Error setting species: ${err.message}`);
    } finally {
      setSettingSpecies(false);
    }
  };

  const handleImageDimensionsLoaded = useCallback(
    (s3Url: string, width: number, height: number) => {
      if (width <= 0 || height <= 0) return;
      setLoadedImageDims((prev) => {
        const existing = prev[s3Url];
        if (existing?.width === width && existing?.height === height) {
          return prev;
        }
        return { ...prev, [s3Url]: { width, height } };
      });
    },
    []
  );

  const selectedWithLoadedDims = useMemo(() => {
    let count = 0;
    selectedImages.forEach((url) => {
      if (loadedImageDims[url]) count += 1;
    });
    return count;
  }, [selectedImages, loadedImageDims]);

  const handleSaveImageDims = async () => {
    if (selectedWithLoadedDims === 0) {
      setError('Select images that have finished loading in the grid');
      return;
    }
    const updates = Array.from(selectedImages)
      .filter((url) => loadedImageDims[url])
      .map((url) => ({
        file_location: url,
        img_width: loadedImageDims[url].width,
        img_height: loadedImageDims[url].height,
      }));

    setSavingImageDims(true);
    setError('');
    try {
      const res = await api.dataviewer.setImageDims({ updates });
      const data = res.data;
      if (data.successful_updates !== undefined && data.successful_updates > 0) {
        showToast(
          'success',
          `Saved dimensions for ${data.successful_updates} image(s)`,
          { dismissible: true }
        );
        if (data.errors && data.errors.length > 0) {
          setError(
            `Save dimensions: ${data.successful_updates} updated; some errors: ${data.errors
              .slice(0, 5)
              .join('; ')}`
          );
        } else {
          setError('');
        }
      } else if (data.errors && data.errors.length > 0) {
        setError(`Save dimensions: ${data.errors.join('; ')}`);
      }
    } catch (err: any) {
      setError(`Error saving image dimensions: ${err.message}`);
    } finally {
      setSavingImageDims(false);
    }
  };

  const handleClearSpecies = async () => {
    if (selectedImages.size === 0) {
      setError('Select at least one image');
      return;
    }
    const urls = Array.from(selectedImages);
    setClearingSpecies(true);
    setError('');
    try {
      const res = await api.dataviewer.clearSpecies({
        image_urls: urls,
      });
      const data = res.data;
      if (data.successful_updates !== undefined && data.successful_updates > 0) {
        setImages((prev) =>
          prev.map((img) => (urls.includes(img.s3_url) ? { ...img, species: null } : img))
        );
        if (data.errors && data.errors.length > 0) {
          setError(`Clear species: ${data.successful_updates} updated; some errors: ${data.errors.slice(0, 5).join('; ')}`);
        } else {
          setError('');
        }
      } else if (data.errors && data.errors.length > 0) {
        setError(`Clear species: ${data.errors.join('; ')}`);
      }
    } catch (err: any) {
      setError(`Error clearing species: ${err.message}`);
    } finally {
      setClearingSpecies(false);
    }
  };

  // Fetch classifications for selected images (for remove-classification dropdown)
  useEffect(() => {
    if (selectedImages.size === 0) {
      setClassificationsForSelectedImages({});
      return;
    }
    const urls = Array.from(selectedImages);
    api.dataviewer.getClassificationsBatch({ image_urls: urls })
      .then((res) => {
        setClassificationsForSelectedImages(res.data.results || {});
      })
      .catch(() => setClassificationsForSelectedImages({}));
  }, [selectedImages]);

  const handleRemoveClassificationBatch = async () => {
    if (!classificationToRemove.trim() || selectedImages.size === 0) return;
    if (!confirmClassificationChange('remove', [classificationToRemove.trim()], `${selectedImages.size} selected image${selectedImages.size === 1 ? '' : 's'}`)) return;
    setRemovingClassification(true);
    setError('');
    try {
      const res = await api.dataviewer.removeClassification({
        image_urls: Array.from(selectedImages),
        classification: classificationToRemove.trim(),
      });
      const data = res.data;
      if (data.errors && data.errors.length > 0) {
        setError(`Remove classification: ${data.successful_updates} updated; errors: ${data.errors.join('; ')}`);
      } else {
        setClassificationToRemove('');
        if (data.successful_updates !== undefined && data.successful_updates > 0) {
          setError('');
          const urls = Array.from(selectedImages);
          const next = await api.dataviewer.getClassificationsBatch({ image_urls: urls });
          setClassificationsForSelectedImages(next.data.results || {});
        }
      }
    } catch (err: any) {
      setError(`Error removing classification: ${err.message}`);
    } finally {
      setRemovingClassification(false);
    }
  };

  // Unique tags from queried data (for suggested tags)
  const suggestedTags = React.useMemo(() => {
    const set = new Set<string>();
    images.forEach((img) => normalizeTags(img.tags).forEach((t: string) => set.add(t)));
    return Array.from(set).sort();
  }, [images]);

  const suggestedSpecies = React.useMemo(() => {
    const set = new Set<string>();
    images.forEach((img) => {
      if (img.species && String(img.species).trim()) {
        set.add(String(img.species).trim());
      }
    });
    return Array.from(set).sort();
  }, [images]);

  const hasImagesWithNoSpecies = React.useMemo(
    () => images.some((img) => !(img.species && String(img.species).trim())),
    [images]
  );

  // Union of tags across currently selected images (for "Remove tag" dropdown)
  const selectedImagesTagsUnion = React.useMemo(() => {
    const set = new Set<string>();
    images
      .filter((img) => selectedImages.has(img.s3_url))
      .forEach((img) => normalizeTags(img.tags).forEach((t: string) => set.add(t)));
    return Array.from(set).sort();
  }, [images, selectedImages]);

  // How many selected images already have the "picsellia" tag (already sent to Picsellia)
  const alreadyInPicselliaCount = React.useMemo(() => {
    return images.filter(
      (img) =>
        selectedImages.has(img.s3_url) &&
        normalizeTags(img.tags)
          .map((t: string) => t.toLowerCase())
          .includes('picsellia')
    ).length;
  }, [images, selectedImages]);
  // Union of classifications across currently selected images (for "Remove classification" dropdown)
  const selectedImagesClassificationsUnion = React.useMemo(() => {
    const set = new Set<string>();
    Array.from(selectedImages).forEach((url) => {
      (classificationsForSelectedImages[url] || []).forEach((c: string) => set.add(c));
    });
    return Array.from(set).sort();
  }, [selectedImages, classificationsForSelectedImages]);

  const selectedImagesSpeciesUnion = React.useMemo(() => {
    const set = new Set<string>();
    images
      .filter((img) => selectedImages.has(img.s3_url))
      .forEach((img) => {
        if (img.species && String(img.species).trim()) {
          set.add(String(img.species).trim());
        }
      });
    return Array.from(set).sort();
  }, [images, selectedImages]);

  // Client-side filter by include/exclude tags and species (after query). Case-insensitive for tags.
  const filteredImages =
    includeTags.length === 0 &&
    excludeTags.length === 0 &&
    includeSpecies.length === 0 &&
    excludeSpecies.length === 0
      ? images
      : images.filter((img) => {
          const imgTags = normalizeTags(img.tags).map((t: string) => t.toLowerCase());
          const includeOk =
            includeTags.length === 0 ||
            includeTags.some((t: string) => imgTags.includes(t.trim().toLowerCase()));
          const excludeOk =
            excludeTags.length === 0 ||
            !excludeTags.some((t: string) => imgTags.includes(t.trim().toLowerCase()));
          const speciesOk = matchesSpeciesFilters(img.species, includeSpecies, excludeSpecies);
          return includeOk && excludeOk && speciesOk;
        });

  // Suggestions: when input empty show all tags not already selected; when typing filter by query
  const includeSuggestions = React.useMemo(() => {
    const notAlreadyIncluded = suggestedTags.filter(
      (tag) => !includeTags.map((t) => t.toLowerCase()).includes(tag.toLowerCase())
    );
    const q = includeTagInput.trim().toLowerCase();
    if (!q) return notAlreadyIncluded;
    return notAlreadyIncluded.filter((tag) => tag.toLowerCase().includes(q));
  }, [suggestedTags, includeTagInput, includeTags]);

  const excludeSuggestions = React.useMemo(() => {
    const notAlreadyExcluded = suggestedTags.filter(
      (tag) => !excludeTags.map((t) => t.toLowerCase()).includes(tag.toLowerCase())
    );
    const q = excludeTagInput.trim().toLowerCase();
    if (!q) return notAlreadyExcluded;
    return notAlreadyExcluded.filter((tag) => tag.toLowerCase().includes(q));
  }, [suggestedTags, excludeTagInput, excludeTags]);

  const includeSpeciesSuggestions = React.useMemo(() => {
    const notAlreadyIncluded = suggestedSpecies.filter(
      (species) => !includeSpecies.map((s) => s.toLowerCase()).includes(species.toLowerCase())
    );
    const q = includeSpeciesInput.trim().toLowerCase();
    if (!q) return notAlreadyIncluded;
    return notAlreadyIncluded.filter((species) => species.toLowerCase().includes(q));
  }, [suggestedSpecies, includeSpeciesInput, includeSpecies]);

  const excludeSpeciesSuggestions = React.useMemo(() => {
    const notAlreadyExcluded = suggestedSpecies.filter(
      (species) => !excludeSpecies.map((s) => s.toLowerCase()).includes(species.toLowerCase())
    );
    const q = excludeSpeciesInput.trim().toLowerCase();
    if (!q) return notAlreadyExcluded;
    return notAlreadyExcluded.filter((species) => species.toLowerCase().includes(q));
  }, [suggestedSpecies, excludeSpeciesInput, excludeSpecies]);

  // Pagination or infinite scroll
  const totalPages = Math.ceil(filteredImages.length / imagesPerPage);
  const startIndex = (currentPage - 1) * imagesPerPage;
  const endIndex = startIndex + imagesPerPage;
  
  // For infinite scroll, show images up to visibleImageCount
  // For pagination, show images for current page
  const displayedImages = useInfiniteScroll 
    ? filteredImages.slice(0, visibleImageCount)
    : filteredImages.slice(startIndex, endIndex);

  const filteredImagesRef = React.useRef(filteredImages);
  filteredImagesRef.current = filteredImages;

  // Ctrl+A / Cmd+A: select all images that match current filters (not only current page)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'a') return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable) {
          return;
        }
      }
      const list = filteredImagesRef.current;
      if (list.length === 0) return;
      e.preventDefault();
      setSelectedImages(new Set(list.map((img) => img.s3_url)));
      setLastSelectedIndex(list.length - 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  
  // Infinite scroll handler - listens to container scroll, not window scroll
  useEffect(() => {
    if (!useInfiniteScroll || !imageContainerRef.current) return;
    
    const container = imageContainerRef.current;
    
    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;
      
      // Load more when near bottom (within 200px)
      if (scrollTop + containerHeight >= scrollHeight - 200) {
        if (visibleImageCount < filteredImages.length) {
          setVisibleImageCount(prev => Math.min(prev + 50, filteredImages.length));
        }
      }
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [useInfiniteScroll, visibleImageCount, filteredImages.length]);
  
  // Reset visible count when switching to infinite scroll or when images change
  useEffect(() => {
    if (useInfiniteScroll) {
      setVisibleImageCount(50);
    } else {
      setCurrentPage(1);
    }
  }, [useInfiniteScroll, images.length]);
  
  const bumpFilterVersion = () => setFilterVersion((v) => v + 1);

  const addIncludeTag = (tag: string) => {
    const t = String(tag).trim();
    if (!t) return;
    setIncludeTags((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setIncludeTagInput('');
    bumpFilterVersion();
  };
  const removeIncludeTag = (tag: string) => {
    setIncludeTags((prev) => prev.filter((x) => x !== tag));
    bumpFilterVersion();
  };
  const addExcludeTag = (tag: string) => {
    const t = String(tag).trim();
    if (!t) return;
    setExcludeTags((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setExcludeTagInput('');
    bumpFilterVersion();
  };
  const removeExcludeTag = (tag: string) => {
    setExcludeTags((prev) => prev.filter((x) => x !== tag));
    bumpFilterVersion();
  };
  const addIncludeSpecies = (species: string) => {
    if (species === SPECIES_NONE_TOKEN) {
      setIncludeSpecies((prev) => (prev.includes(SPECIES_NONE_TOKEN) ? prev : [...prev, SPECIES_NONE_TOKEN]));
      setIncludeSpeciesInput('');
      bumpFilterVersion();
      return;
    }
    const v = String(species).trim();
    if (!v) return;
    setIncludeSpecies((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setIncludeSpeciesInput('');
    bumpFilterVersion();
  };
  const removeIncludeSpecies = (species: string) => {
    setIncludeSpecies((prev) => prev.filter((x) => x !== species));
    bumpFilterVersion();
  };

  const addExcludeSpecies = (species: string) => {
    if (species === SPECIES_NONE_TOKEN) {
      setExcludeSpecies((prev) => (prev.includes(SPECIES_NONE_TOKEN) ? prev : [...prev, SPECIES_NONE_TOKEN]));
      setExcludeSpeciesInput('');
      bumpFilterVersion();
      return;
    }
    const v = String(species).trim();
    if (!v) return;
    setExcludeSpecies((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setExcludeSpeciesInput('');
    bumpFilterVersion();
  };
  const removeExcludeSpecies = (species: string) => {
    setExcludeSpecies((prev) => prev.filter((x) => x !== species));
    bumpFilterVersion();
  };

  // Handle image selection with shift-click support
  const handleImageSelect = (s3Url: string, index: number, event: React.MouseEvent) => {
    const newSelected = new Set(selectedImages);
    
    if (event.shiftKey && lastSelectedIndex >= 0) {
      // Shift-click: select range
      const startIdx = Math.min(lastSelectedIndex, index);
      const endIdx = Math.max(lastSelectedIndex, index);
      for (let i = startIdx; i <= endIdx; i++) {
        if (i < filteredImages.length) {
          newSelected.add(filteredImages[i].s3_url);
        }
      }
    } else {
      if (newSelected.has(s3Url)) {
        newSelected.delete(s3Url);
      } else {
        newSelected.add(s3Url);
      }
      setLastSelectedIndex(index);
    }
    
    setSelectedImages(newSelected);
  };

  return (
    <div className="plot-viewer">
      <ETLRunsSection
        searchRunName={searchRunName}
        setSearchRunName={setSearchRunName}
        searchStartDate={searchStartDate}
        setSearchStartDate={setSearchStartDate}
        searchEndDate={searchEndDate}
        setSearchEndDate={setSearchEndDate}
        loadETLRuns={loadETLRuns}
        loadingRuns={loadingRuns}
        runsError={runsError}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        datesWithRunStarts={datesWithRunStarts}
        datesWithData={datesWithData}
        filteredETLRuns={filteredETLRuns}
        onRunClick={handleRunClick}
      />

      <div style={{ marginTop: '2rem' }}>
        <h2>Image Viewer</h2>
        <QueryFormSection
          robotName={robotName}
          setRobotName={setRobotName}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          queryTemplates={queryTemplates}
          selectedQueryTemplate={selectedQueryTemplate}
          setSelectedQueryTemplate={setSelectedQueryTemplate}
          onQuery={handleQuery}
          loading={loading}
          jobId={jobId}
          jobStatus={jobStatus}
          error={error}
        />

          {images.length > 0 && (
            <div className="image-viewer">
              <div className="image-viewer-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
                <TagFilterRow
                  suggestedTags={suggestedTags}
                  includeTags={includeTags}
                  includeTagInput={includeTagInput}
                  setIncludeTagInput={setIncludeTagInput}
                  addIncludeTag={addIncludeTag}
                  removeIncludeTag={removeIncludeTag}
                  includeSuggestions={includeSuggestions}
                  excludeTags={excludeTags}
                  excludeTagInput={excludeTagInput}
                  setExcludeTagInput={setExcludeTagInput}
                  addExcludeTag={addExcludeTag}
                  removeExcludeTag={removeExcludeTag}
                  excludeSuggestions={excludeSuggestions}
                />
                <SpeciesFilterRow
                  suggestedSpecies={suggestedSpecies}
                  hasImagesWithNoSpecies={hasImagesWithNoSpecies}
                  includeSpecies={includeSpecies}
                  includeSpeciesInput={includeSpeciesInput}
                  setIncludeSpeciesInput={setIncludeSpeciesInput}
                  addIncludeSpecies={addIncludeSpecies}
                  removeIncludeSpecies={removeIncludeSpecies}
                  includeSpeciesSuggestions={includeSpeciesSuggestions}
                  excludeSpecies={excludeSpecies}
                  excludeSpeciesInput={excludeSpeciesInput}
                  setExcludeSpeciesInput={setExcludeSpeciesInput}
                  addExcludeSpecies={addExcludeSpecies}
                  removeExcludeSpecies={removeExcludeSpecies}
                  excludeSpeciesSuggestions={excludeSpeciesSuggestions}
                />

                {selectedImages.size > 0 ? (
                  <SelectionActionsRows
                    selectedCount={selectedImages.size}
                    tagInput={tagInput}
                    setTagInput={setTagInput}
                    onAddTags={handleAddTags}
                    addingTags={addingTags}
                    tagToRemove={tagToRemove}
                    setTagToRemove={setTagToRemove}
                    selectedImagesTagsUnion={selectedImagesTagsUnion}
                    onRemoveTag={handleRemoveTag}
                    removingTag={removingTag}
                    onUploadToPicsellia={handleUploadToPicsellia}
                    activePicselliaUploadCount={activePicselliaUploads}
                    alreadyInPicselliaCount={alreadyInPicselliaCount}
                    onDeselectAll={() => setSelectedImages(new Set())}
                    classificationBatchInput={classificationBatchInput}
                    setClassificationBatchInput={setClassificationBatchInput}
                    onAddClassificationsBatch={handleAddClassificationsBatch}
                    addingClassificationsBatch={addingClassificationsBatch}
                    classificationToRemove={classificationToRemove}
                    setClassificationToRemove={setClassificationToRemove}
                    selectedImagesClassificationsUnion={selectedImagesClassificationsUnion}
                    onRemoveClassificationBatch={handleRemoveClassificationBatch}
                    removingClassification={removingClassification}
                    speciesInput={speciesInput}
                    setSpeciesInput={setSpeciesInput}
                    onSetSpecies={handleSetSpecies}
                    settingSpecies={settingSpecies}
                    onClearSpecies={handleClearSpecies}
                    clearingSpecies={clearingSpecies}
                    selectedImagesSpeciesUnion={selectedImagesSpeciesUnion}
                    selectedWithLoadedDims={selectedWithLoadedDims}
                    onSaveImageDims={handleSaveImageDims}
                    savingImageDims={savingImageDims}
                  />
                ) : null}
              </div>
              {imageLoading && <div className="loading">Loading images...</div>}
              <div className="image-groups">
                <ImageGridSection
                  filteredCount={filteredImages.length}
                  totalCount={images.length}
                  onRefresh={handleQuery}
                  refreshDisabled={loading || !robotName || !startDate || !endDate}
                  useInfiniteScroll={useInfiniteScroll}
                  setUseInfiniteScroll={setUseInfiniteScroll}
                  filterVersion={filterVersion}
                  imageContainerRef={imageContainerRef}
                  displayedImages={displayedImages}
                  presignedUrls={presignedUrls}
                  selectedImages={selectedImages}
                  onImageSelect={handleImageSelect}
                  onImageDoubleClick={(s3Url: string, presignedUrl: string) => setSelectedImage({ url: s3Url, presignedUrl })}
                  onImageDimensionsLoaded={handleImageDimensionsLoaded}
                  totalPages={totalPages}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  startIndex={startIndex}
                  endIndex={endIndex}
                />
              </div>
            </div>
          )}
      </div>

      <ImageLightbox
        selectedImage={selectedImage}
        onClose={() => { setSelectedImage(null); setQueryImageData(null); }}
        queryImageData={queryImageData}
        queryImageDataLoading={queryImageDataLoading}
        classificationInput={classificationInput}
        setClassificationInput={setClassificationInput}
        currentClassificationNames={currentClassificationNames}
        onAddClassification={addClassification}
        onRemoveClassification={removeClassification}
        savingClassifications={savingClassifications}
      />
    </div>
  );
};

export default DataViewer;
